import type { OnboardingPhase } from "@/features/onboarding/api/use-onboarding";

export interface HomeStep {
	readonly key: string;
	readonly done: boolean;
	readonly titleKey: string;
	readonly bodyKey: string;
	readonly actionKey: string;
	readonly to: string;
	readonly external?: boolean;
}

export interface HomeStepFacts {
	readonly hasStructure: boolean;
	readonly identityOnly?: boolean;
	readonly onboardingPhase: OnboardingPhase | null;
	readonly publishedChatUrl: string | null;
	readonly publishedChatAnswerer: string | null;
	readonly chatFunnelLinked: boolean | null;
	readonly hasThreads: boolean;
	readonly hasAssumedThread: boolean;
	readonly knowsTheBusiness: boolean;
	readonly openGaps: number | null;
	readonly sitePublished: boolean;
	/** Uma conversa já passou pelo chat do site — o teste da etapa do chat (23/10). */
	readonly chatTested: boolean;
	/** O site foi pedido (a missão da trilha) ou já tem rascunho: a etapa segue sem esperar ficar pronto. */
	readonly siteRequested: boolean;
}

/** O passo de AGORA: o primeiro por fazer. A home e a faixa da jornada leem daqui, e só daqui. */
export function nextStep(steps: readonly HomeStep[]): HomeStep | undefined {
	return steps.find((step) => !step.done);
}

/** O fim da trilha: o que foi montado, com os endereços (23/10). */
export const JOURNEY_DONE = "/comecar/pronto";

/** Para onde a trilha leva a seguir: o próximo passo por fazer ou, sem nenhum, o fim. */
export function journeyNext(
	steps: readonly HomeStep[],
): string | { path: string; query: { onboarding: string } } {
	const step = nextStep(steps);
	if (!step) return JOURNEY_DONE;
	return step.to.startsWith("/comecar") ? step.to : { path: step.to, query: { onboarding: "1" } };
}

export function homeSteps(facts: HomeStepFacts): HomeStep[] {
	// Done beats phase: a workspace with structure does not hear about the first
	// day again, even if the proposal draft still exists in `files`. All the
	// phases point at `/comecar`, because that is where each one draws its screen.
	// Pulado conta como feito: quem escolheu montar sozinho segue para o passo seguinte.
	const built =
		(!facts.identityOnly && facts.hasStructure) ||
		facts.onboardingPhase === "applied" ||
		facts.onboardingPhase === "skipped";
	const phase = built ? "idle" : (facts.onboardingPhase ?? "idle");
	const first = facts.identityOnly ? "steps.identity" : `steps.first.${phase}`;

	return [
		{
			key: "structure",
			done: built,
			to: "/comecar",
			titleKey: `${first}.title`,
			bodyKey: `${first}.body`,
			actionKey: `${first}.action`,
		},
		{
			key: "chat",
			// Ligado E testado (dono, 23/10): sem uma conversa de verdade a etapa não fecha.
			done: facts.chatFunnelLinked === true && facts.chatTested,
			titleKey: "steps.chat.title",
			bodyKey: facts.chatFunnelLinked === true ? "steps.chat.talkBody" : "steps.chat.body",
			actionKey: facts.chatFunnelLinked === true ? "steps.chat.talkAction" : "steps.chat.action",
			to: "/comecar/chat",
		},
		{
			key: "site",
			// Pedir já fecha (dono, 23/10 — antes só a publicação, QA-024): o site constrói-se
			// enquanto a pessoa segue, e o fim da trilha mostra-o para publicar.
			done: facts.sitePublished || facts.siteRequested,
			to: "/site",
			titleKey: "steps.site.title",
			bodyKey: "steps.site.body",
			actionKey: "steps.site.action",
		},
		{
			key: "inbox",
			done: facts.hasAssumedThread,
			titleKey: "steps.inbox.title",
			bodyKey: "steps.inbox.body",
			actionKey: "steps.inbox.action",
			to: "/inbox",
		},
		{
			key: "gaps",
			done: facts.knowsTheBusiness && facts.openGaps === 0,
			titleKey: "steps.gaps.title",
			bodyKey: "steps.gaps.body",
			actionKey: "steps.gaps.action",
			to: "/help",
		},
	];
}
